import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Camera, RefreshCw } from "lucide-react";
import { useState } from "react";
import { post } from "../../lib/api";
import { Card } from "../ui";
import { eur, eurShort, errText, fmtDay, fmtMonth, WK, type Snapshot, type WealthDoc } from "./shared";

export function HistoryTab({ snapshots, isPhone }: { snapshots: Snapshot[]; isPhone: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  // A manual snapshot replaces this month's automatic one
  const takeSnapshot = async () => {
    setBusy(true);
    try {
      const r = await post<{ ok: boolean; snapshots: Snapshot[] }>("/api/wealth/snapshot", {});
      qc.setQueryData<WealthDoc>(WK.doc, (d) => (d ? { ...d, snapshots: r.snapshots } : d));
    } catch (e) {
      window.alert(`Snapshot failed: ${errText(e)}`);
    }
    setBusy(false);
  };
  const snaps = snapshots.slice(-(isPhone ? 8 : 18));
  const max = Math.max(...snaps.map((s) => s.total), 1);
  return (
    <Card
      title={<>Net worth over time <span className="muted ml-1 hidden text-xs font-normal sm:inline">one snapshot a month, taken automatically</span></>}
      icon={<RefreshCw size={16} />}
      actions={<button className="btn btn-sm" disabled={busy} onClick={() => void takeSnapshot()}><Camera size={14} /> Snapshot now</button>}
    >
      {snapshots.length === 0 && <p className="muted text-sm">No snapshots yet — the first is taken automatically this month.</p>}
      {snapshots.length > 0 && (
        <>
          <div className="flex h-44 items-end gap-1 sm:gap-1.5">
            {snaps.map((s, i) => (
              <div key={s.at} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1" title={`${fmtDay(s.at.slice(0, 10))}: ${eur(s.total)}`}>
                <span className="muted truncate text-[10px] num">{eurShort(s.total)}</span>
                <div className={clsx("w-full max-w-[34px] rounded-t", i === snaps.length - 1 ? "bg-brand-600" : "bg-brand-200")} style={{ height: `${Math.max(2, (s.total / max) * 100)}%` }} />
                <span className="muted text-[10.5px]">{fmtMonth(s.at)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="table min-w-[620px]">
              <thead><tr><th>Month</th><th className="!text-right">Net worth</th><th className="!text-right">Change</th><th className="!text-right">Cash</th><th className="!text-right">Investments</th><th className="!text-right">Property</th></tr></thead>
              <tbody>
                {[...snapshots].reverse().slice(0, 24).map((s, i, arr) => {
                  const prev = arr[i + 1];
                  const delta = prev ? s.total - prev.total : null;
                  return (
                    <tr key={s.at}>
                      <td>{fmtDay(s.at.slice(0, 10))}</td>
                      <td className="text-right num">{eur(s.total)}</td>
                      <td className="text-right num" style={{ color: delta == null ? undefined : delta >= 0 ? "#065f46" : "#b91c1c" }}>{delta == null ? "—" : `${delta >= 0 ? "+" : "−"}${eur(Math.abs(delta))}`}</td>
                      <td className="text-right num">{eur(s.by?.cash || 0)}</td>
                      <td className="text-right num">{eur(s.by?.investment || 0)}</td>
                      <td className="text-right num">{eur(s.by?.property || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Card>
  );
}
